// RESONATE_PERF_V3 - deferred mount that never blocks scroll.
// Sections mount either when they scroll near the viewport (IntersectionObserver)
// OR automatically after first paint (window load + idle), whichever comes first.
// This keeps the initial paint hero-only while ensuring the rest of the page is
// ready before the user reaches it - no flash / pop-in while scrolling.
import { useEffect, useRef, useState } from "react";

export const LazyMount = ({ children, rootMargin = "400px", minHeight = 400 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;

    let idleId;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin, threshold: 0 }
    );
    io.observe(el);

    // Preload after first paint: once the page has loaded and the main thread
    // is idle, mount regardless of scroll position.
    const preload = () => {
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(() => setVisible(true), { timeout: 2500 });
      } else {
        idleId = setTimeout(() => setVisible(true), 600);
      }
    };
    if (document.readyState === "complete") {
      preload();
    } else {
      window.addEventListener("load", preload, { once: true });
    }

    return () => {
      io.disconnect();
      window.removeEventListener("load", preload);
      if (idleId) {
        if ("cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
        else clearTimeout(idleId);
      }
    };
  }, [rootMargin]);

  return (
    <div ref={ref} style={visible ? undefined : { minHeight }}>
      {visible ? children : null}
    </div>
  );
};
